import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import type { LevelReading, LevelWindow } from "@/lib/audio-level-windows";
import type { PreferredDevices } from "@/utils/devices";

import { capturePhoto as capturePhotoUtil } from "./capture-photo";
import { useDeviceChangeBreadcrumbs } from "./device-change-diagnostics";
import {
  createRecordingSpan,
  type RecordingDiagnostics,
  type RecordingSpan,
  type TrackDiagnostics,
  type TrackOutcome,
  type VideoOutcome,
} from "./recording-span";
import {
  captureWebcamWarning,
  reportAudioGraphNotRunning,
  type ReportedSurface,
  reportRecordingStarted,
  reportVideoProfileResolved,
  webcamBreadcrumb,
} from "./telemetry";
import { GRAPH_READY_TIMEOUT_MS, useAudioGraph } from "./use-audio-graph";
import { type RecordingError, useMediaRecorder } from "./use-media-recorder";
import {
  type AudioDeviceSnapshot,
  type BrowserSupport,
  describeAudioDevice,
  type MediaTrackSummary,
  type StreamError,
  summarizeTracks,
  type TrackStatusInfo,
  type TrackSummary,
  useMediaStream,
  type UseMediaStreamReturn,
} from "./use-media-stream";
import { useVideoProfile } from "./use-video-profile";
import type { CapturedMedia } from "./utils";
import { STANDARD_VIDEO_PROFILE, type VideoProfile } from "./video-profile";

export type {
  AudioDeviceSnapshot,
  BrowserSupport,
  CapturedMedia,
  MediaTrackSummary,
  RecordingDiagnostics,
  TrackDiagnostics,
  TrackOutcome,
  TrackStatusInfo,
  TrackSummary,
  VideoOutcome,
};

// ─── Public types ──────────────────────────────────────────────────────────────

/**
 * Capture mode.
 * - "photo"  — canvas snapshot; no audio track acquired.
 * - "video"  — MediaRecorder with camera + mic.
 * - "audio"  — MediaRecorder, audio-only; camera toggle is suppressed.
 */
export type CaptureMode = "photo" | "video" | "audio";

/**
 * Lifecycle states for a capture session.
 *
 * ```
 * loading ──► ready ──► started ──► recording ──► started
 *   │           │           │                       │
 *   ▼           ▼           └──── capturePhoto ─────┤
 * (video     error                                   ▼
 *  element)                                      recorded
 *                                                    │
 *                                               discardCapture
 *                                                    │
 *                                                    ▼
 *                                                  ready
 * ```
 */
export type WebcamCaptureState =
  | "loading"
  | "ready"
  | "error"
  | "started"
  | "recording"
  | "recorded";

export type CaptureErrorKind =
  | StreamError["kind"]
  | RecordingError["kind"]
  | "photo_failed";

export interface CaptureError {
  kind: CaptureErrorKind;
  message: string;
  cause?: unknown;
}

export interface RecordingConfig {
  /**
   * Maximum recording duration in seconds.
   * @default 240 (4 minutes)
   */
  maxDurationSeconds?: number;
  /**
   * Bitrate in bits per second applied to the recorded track.
   * Defaults to 1.5 Mbps for video and 128 kbps for audio when not set.
   */
  bitrate?: number;
  /**
   * Interval in milliseconds at which recorded data is flushed.
   * @default 1000
   */
  timeslice?: number;
}

export interface UseWebcamCaptureOptions {
  /**
   * Maximum allowed file size in bytes.
   * @default 200MB
   */
  maxFileSize?: number;
  /**
   * Initial capture mode.
   * @default "photo"
   */
  initialMode?: CaptureMode;
  /**
   * Start the stream automatically on mount.
   * @default false
   */
  autoStart?: boolean;
  /**
   * Whether the camera (video track) is enabled. Ignored in "audio" mode.
   * @default true
   */
  cameraEnabled?: boolean;
  /** Initial preferred camera / microphone devices. */
  preferredDevices?: PreferredDevices;
  /**
   * Labels the telemetry this hook raises itself. Supplied by
   * `useWebcamCaptureTelemetry`, so spreading its result sets this too.
   * @default "unknown"
   */
  captureSurface?: ReportedSurface;
  /**
   * Socratic dialogue this capture belongs to. Also supplied by
   * `useWebcamCaptureTelemetry`; tags the audio-graph events.
   */
  dialogueId?: string;
  /** Recording configuration overrides. */
  recordingConfig?: RecordingConfig;
  /** Called once when a recording actually starts (`recorder.start()` fired). */
  onRecordingStarted?: () => void;
  /**
   * Called once when captured media becomes available.
   * @param media       The captured file.
   * @param duration    Elapsed recording time in whole seconds (0 for photos).
   * @param diagnostics  Track health over the recording (omitted for photos).
   */
  onMediaCaptured?: (
    media: CapturedMedia,
    duration: number,
    diagnostics?: RecordingDiagnostics
  ) => void;
  /** Forwarded track end/mute/unmute events on the active stream. */
  onTrackStatusChange?: (info: TrackStatusInfo) => void;
  /** Forwarded getUserMedia failure (permission, device not found, etc.). */
  onStreamError?: (error: StreamError) => void;
  /** Forwarded MediaRecorder failure (codec unsupported, recorder error). */
  onRecordingError?: (error: RecordingError) => void;
  /**
   * Called after a lost microphone is automatically replaced with the system's
   * current default mic. `label` is the new device's label (may be empty).
   */
  onAudioRecovered?: (label: string) => void;
  /** Called when a lost microphone could not be replaced (none available). */
  onAudioRecoveryFailed?: () => void;
  /**
   * Every audio sample, for callers that must react during the recording rather
   * than after it — telling a student we cannot hear them, or a device check.
   */
  onLevelReading?: (reading: LevelReading) => void;
  /** Audio levels aggregated into 10 s windows, for the diagnostic trail. */
  onLevelWindow?: (window: LevelWindow) => void;
}

export interface UseWebcamCaptureReturn {
  // ── State machine ─────────────────────────────────────────────────────────
  state: WebcamCaptureState;
  capturedMedia: CapturedMedia | null;
  /** Set when state === 'error' or on a non-fatal capture failure. */
  error: CaptureError | null;

  // ── Convenience ───────────────────────────────────────────────────────────
  mode: CaptureMode;
  isRecording: boolean;
  /** true while a stopped capture is being flattened before upload. */
  isProcessing: boolean;
  recordingDuration: number;
  maxDurationSeconds: number;
  /** true when state is 'started' | 'recording' | 'recorded'. */
  hasPermission: boolean;
  isVideoElementReady: boolean;
  isStartingCamera: boolean;
  stream: MediaStream | null;
  browserSupport: BrowserSupport;
  /** true when the camera (video track) is active in the current stream. */
  cameraEnabled: boolean;
  /** true when the microphone is unmuted. */
  micEnabled: boolean;

  // ── Actions ───────────────────────────────────────────────────────────────
  startCamera: (preferredDevices?: PreferredDevices) => Promise<void>;
  stopCamera: () => void;
  capturePhoto: () => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
  setMode: (mode: CaptureMode) => void;
  discardCapture: () => void;
  /**
   * Toggle camera on/off. Only effective in "video" mode and outside of
   * recording. Restarts the stream if one is already active.
   */
  toggleCamera: () => void;
  /**
   * Mute/unmute the microphone. No-op while recording.
   * Mutates the `enabled` flag on audio tracks — does not restart the stream.
   */
  toggleMic: () => void;

  // ── Refs ──────────────────────────────────────────────────────────────────
  videoRef: (node: HTMLVideoElement | null) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_RECORDING_CONFIG = {
  maxDurationSeconds: 240,
  videoBitrate: STANDARD_VIDEO_PROFILE.bitrate,
  audioBitrate: 128000,
  timeslice: 1000,
} as const;

// ─── Helper ───────────────────────────────────────────────────────────────────

// Photo mode never touches MediaRecorder, so it has no reason to be capped.
function videoProfileForMode(
  mode: CaptureMode,
  profile: VideoProfile
): VideoProfile {
  return mode === "video" ? profile : STANDARD_VIDEO_PROFILE;
}

function buildStreamConstraints(
  mode: CaptureMode,
  cameraEnabled: boolean,
  preferred: PreferredDevices | undefined,
  baseVideoConstraints: MediaTrackConstraints
): {
  videoConstraints: MediaTrackConstraints | false;
  audioConstraints: MediaTrackConstraints | false;
} {
  // "audio" mode always suppresses the video track regardless of cameraEnabled.
  const wantCamera = mode !== "audio" && cameraEnabled;

  const videoConstraints: MediaTrackConstraints | false = wantCamera
    ? {
        ...baseVideoConstraints,
        ...(preferred?.video
          ? { deviceId: { exact: preferred.video.id } }
          : {}),
      }
    : false;

  // Photo mode: no audio needed for a canvas snapshot.
  const audioConstraints: MediaTrackConstraints | false =
    mode !== "photo"
      ? preferred?.audio
        ? { deviceId: { exact: preferred.audio.id } }
        : {}
      : false;

  return { videoConstraints, audioConstraints };
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

interface CaptureReducerState {
  captureState: WebcamCaptureState;
  capturedMedia: CapturedMedia | null;
  error: CaptureError | null;
  mode: CaptureMode;
  cameraEnabled: boolean;
  micEnabled: boolean;
}

type CaptureAction =
  | { type: "STREAM_STARTED" }
  | { type: "STREAM_STOPPED" }
  | { type: "STREAM_ERROR"; error: CaptureError }
  | { type: "RECORDING_STARTED" }
  | { type: "MEDIA_CAPTURED"; media: CapturedMedia }
  | { type: "CAPTURE_ERROR"; error: CaptureError }
  | { type: "VIDEO_ELEMENT_CHANGED"; ready: boolean }
  | { type: "DISCARD" }
  | { type: "SET_MODE"; mode: CaptureMode }
  | { type: "TOGGLE_CAMERA" }
  | { type: "TOGGLE_MIC" };

function captureReducer(
  state: CaptureReducerState,
  action: CaptureAction
): CaptureReducerState {
  switch (action.type) {
    case "STREAM_STARTED":
      return { ...state, captureState: "started", error: null };
    case "STREAM_STOPPED":
      return {
        ...state,
        captureState:
          state.captureState === "started" ? "ready" : state.captureState,
      };
    case "STREAM_ERROR":
      return { ...state, error: action.error, captureState: "error" };
    case "RECORDING_STARTED":
      return { ...state, captureState: "recording" };
    case "MEDIA_CAPTURED":
      return {
        ...state,
        capturedMedia: action.media,
        captureState: "recorded",
      };
    case "CAPTURE_ERROR":
      // The recorder is torn down but the stream is still live
      return { ...state, error: action.error, captureState: "started" };
    case "VIDEO_ELEMENT_CHANGED": {
      const { ready } = action;
      if (ready && state.captureState === "loading")
        return { ...state, captureState: "ready" };
      if (
        !ready &&
        (state.captureState === "ready" || state.captureState === "error")
      )
        return { ...state, captureState: "loading" };
      return state;
    }
    case "DISCARD":
      if (state.capturedMedia == null) return state;
      return { ...state, capturedMedia: null, captureState: "ready" };
    case "SET_MODE":
      if (action.mode === state.mode) return state;
      return {
        ...state,
        mode: action.mode,
        capturedMedia: null,
        error: null,
        // Mode change is a clean retry path out of the permission/error screen.
        captureState:
          state.captureState === "error" ? "loading" : state.captureState,
      };
    case "TOGGLE_CAMERA":
      // Photo mode requires the camera; audio mode has no camera. Only video
      // mode supports toggling the camera off mid-session.
      if (state.captureState === "recording" || state.mode !== "video")
        return state;
      return { ...state, cameraEnabled: !state.cameraEnabled };
    case "TOGGLE_MIC":
      if (state.captureState === "recording") return state;
      return { ...state, micEnabled: !state.micEnabled };
    default:
      return state;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Unified hook for photo, video, and audio capture from the user's camera/mic.
 *
 * Composes `useMediaStream`, `useMediaRecorder`, and `capturePhoto` into a
 * single state machine. Camera and microphone can be toggled before recording
 * starts. Toggle calls are no-ops while `state === "recording"`.
 *
 * @example
 * ```tsx
 * const webcam = useWebcamCapture({ initialMode: "video", autoStart: true });
 * return <video ref={webcam.videoRef} autoPlay playsInline muted />;
 * ```
 */
export function useWebcamCapture({
  maxFileSize = 200 * 1024 * 1024,
  initialMode = "photo",
  autoStart = false,
  cameraEnabled: initialCameraEnabled = true,
  preferredDevices: initialPreferredDevices,
  captureSurface = "unknown",
  dialogueId,
  recordingConfig,
  onRecordingStarted,
  onMediaCaptured,
  onTrackStatusChange,
  onStreamError,
  onRecordingError,
  onAudioRecovered,
  onAudioRecoveryFailed,
  onLevelReading,
  onLevelWindow,
}: UseWebcamCaptureOptions = {}): UseWebcamCaptureReturn {
  const resolvedRecordingConfig = useMemo(
    () => ({
      maxDurationSeconds:
        recordingConfig?.maxDurationSeconds ??
        DEFAULT_RECORDING_CONFIG.maxDurationSeconds,
      explicitBitrate: recordingConfig?.bitrate,
      timeslice:
        recordingConfig?.timeslice ?? DEFAULT_RECORDING_CONFIG.timeslice,
    }),
    [
      recordingConfig?.maxDurationSeconds,
      recordingConfig?.bitrate,
      recordingConfig?.timeslice,
    ]
  );

  const [state, dispatch] = useReducer(captureReducer, {
    captureState: "loading",
    mode: initialMode,
    capturedMedia: null,
    error: null,
    cameraEnabled: initialCameraEnabled,
    micEnabled: true,
  });
  const {
    captureState,
    mode,
    capturedMedia,
    error,
    cameraEnabled,
    micEnabled,
  } = state;

  const lastPreferredDevicesRef = useRef(initialPreferredDevices);

  // Video profile (resolution + bitrate), probed once via the Media
  // Capabilities API — see video-profile.ts.
  const {
    profile: probedVideoProfile,
    resolved: resolvedVideoProfile,
    getProfile: getVideoProfile,
  } = useVideoProfile();

  useEffect(() => {
    if (resolvedVideoProfile == null) return;
    reportVideoProfileResolved(resolvedVideoProfile);
  }, [resolvedVideoProfile]);

  const activeVideoProfile = videoProfileForMode(
    mode,
    probedVideoProfile ?? STANDARD_VIDEO_PROFILE
  );

  const callbacksRef = useRef({
    onRecordingStarted,
    onMediaCaptured,
    onTrackStatusChange,
    onStreamError,
    onRecordingError,
    onAudioRecovered,
    onAudioRecoveryFailed,
    onLevelReading,
    onLevelWindow,
  });
  useEffect(() => {
    callbacksRef.current = {
      onRecordingStarted,
      onMediaCaptured,
      onTrackStatusChange,
      onStreamError,
      onRecordingError,
      onAudioRecovered,
      onAudioRecoveryFailed,
      onLevelReading,
      onLevelWindow,
    };
  });

  const spanRef = useRef<RecordingSpan>(createRecordingSpan());

  const handleLevelReading = useCallback((reading: LevelReading) => {
    callbacksRef.current.onLevelReading?.(reading);
  }, []);

  const handleLevelWindow = useCallback((window: LevelWindow) => {
    spanRef.current.addLevelWindow(window);
    callbacksRef.current.onLevelWindow?.(window);
  }, []);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Effective camera state ─────────────────────────────────────────────────
  // "audio" mode suppresses the video track regardless of the toggle.

  const effectiveCameraEnabled = mode !== "audio" && cameraEnabled;

  // ── Constraints ───────────────────────────────────────────────────────────
  // Recomputed each render so constraintsRef inside useMediaStream stays
  // current. Mode/camera changes that trigger restarts pass explicit overrides
  // to startStream(); these serve as the fallback for autoStart.

  const streamConstraints = buildStreamConstraints(
    mode,
    effectiveCameraEnabled,
    lastPreferredDevicesRef.current,
    activeVideoProfile.constraints
  );

  // ── Stream ────────────────────────────────────────────────────────

  const recoverAudioRef = useRef<
    UseMediaStreamReturn["recoverAudioTrack"] | null
  >(null);

  const handleAudioLost = async () => {
    const recover = recoverAudioRef.current;

    if (recover == null) return;
    // Empty constraints request the current default device, so a disconnected
    // preferred mic falls back to whatever the OS switched to (e.g. built-in).
    const result = await recover({});

    if (result.ok && result.label != null) {
      callbacksRef.current.onAudioRecovered?.(result.label);
    } else if (!result.ok) {
      callbacksRef.current.onAudioRecoveryFailed?.();
    }
  };

  const mediaStream = useMediaStream({
    videoConstraints: streamConstraints.videoConstraints,
    audioConstraints: streamConstraints.audioConstraints,
    // Held back until the probe settles: the autoStart path inside
    // useMediaStream reads the constraint props, which carry the standard
    // profile until then. Every other start passes resolved constraints.
    autoStart: autoStart && probedVideoProfile != null,
    onStreamStarted() {
      dispatch({ type: "STREAM_STARTED" });
    },
    onStreamStopped() {
      dispatch({ type: "STREAM_STOPPED" });
    },
    onStreamError(streamError) {
      dispatch({ type: "STREAM_ERROR", error: streamError });
      callbacksRef.current.onStreamError?.(streamError);
    },
    onTrackStatusChange(info) {
      callbacksRef.current.onTrackStatusChange?.(info);
      if (info.kind === "audio" && info.status === "ended") {
        void handleAudioLost();
      }
    },
  });

  const {
    startStream,
    stopStream,
    stream,
    isVideoElementReady,
    videoRef: mediaStreamVideoRef,
    browserSupport: streamSupport,
    isStartingCamera,
    setAudioEnabled,
    recoverAudioTrack,
  } = mediaStream;
  recoverAudioRef.current = recoverAudioTrack;

  // ── Gapless audio routing ────────────────────────────────────────
  // Routes the recorder's audio through a constant destination track so the
  // microphone can be swapped mid-recording. Disabled for photo mode.
  const audioGraph = useAudioGraph(stream, {
    enabled: mode !== "photo",
    captureSurface,
    dialogueId,
    onLevelReading: onLevelReading == null ? undefined : handleLevelReading,
    onLevelWindow: onLevelWindow == null ? undefined : handleLevelWindow,
  });
  const recordingStream = audioGraph.recordingStream;

  // ── Recorder  ──────────────────────────────────────────────────────
  // Derive kind from the recording stream so the correct codec is selected even
  // if a camera-toggle renegotiation is still in flight.

  const recordingKind =
    recordingStream != null && recordingStream.getVideoTracks().length > 0
      ? "video"
      : "audio";

  // The capped profile overrides any explicit bitrate — it's a hard technical ceiling, not a preference.
  const effectiveBitrate =
    recordingKind === "video" && activeVideoProfile.kind === "capped"
      ? activeVideoProfile.bitrate
      : (resolvedRecordingConfig.explicitBitrate ??
        (recordingKind === "audio"
          ? DEFAULT_RECORDING_CONFIG.audioBitrate
          : DEFAULT_RECORDING_CONFIG.videoBitrate));

  const recorder = useMediaRecorder({
    stream: recordingStream,
    kind: recordingKind,
    bitrate: effectiveBitrate,
    timeslice: resolvedRecordingConfig.timeslice,
    maxFileSize,
    maxDurationSeconds: resolvedRecordingConfig.maxDurationSeconds,
    onRemuxStart({ inputBytes }) {
      webcamBreadcrumb("remux started", {
        recording_kind: recordingKind,
        input_bytes: inputBytes,
      });
    },
    onRemuxFallback(error) {
      webcamBreadcrumb("remux fell back to raw upload", {
        recording_kind: recordingKind,
      });
      captureWebcamWarning(
        "Web capture remux failed; uploaded raw file",
        { recording_kind: recordingKind },
        { error }
      );
    },
    onCodecFallback({ from, to }) {
      const tags = {
        recording_kind: recordingKind,
        codec_from: from,
        codec_to: to,
      };
      webcamBreadcrumb("codec fell back after an encoder failure", tags);
      captureWebcamWarning(
        "Web capture encoder failed; retried on the next codec",
        tags,
        { message: `MediaRecorder could not encode ${from}` }
      );
    },
    onRecordingStarted(recordedStream, { mimeType }) {
      reportRecordingStarted(captureSurface, {
        recordingKind,
        profile: activeVideoProfile,
        bitrate: effectiveBitrate,
        mimeType,
      });
      spanRef.current.begin({
        recordedStream,
        rawStream: stream,
        cameraEnabled: effectiveCameraEnabled,
        audioDevice: describeAudioDevice(stream),
        startedAtMs: Date.now(),
        measureLevels: callbacksRef.current.onLevelWindow != null,
      });
      dispatch({ type: "RECORDING_STARTED" });
      callbacksRef.current.onRecordingStarted?.();
    },
    onRecordingStopped() {
      spanRef.current.end();
    },
    onMediaCaptured(media, duration) {
      // Read the stream before stopStream() tears it down below.
      const diagnostics = spanRef.current.takeDiagnostics({
        rawTracks: summarizeTracks(stream),
        recordedAudio: summarizeTracks(spanRef.current.recordedStream()).audio,
        contextState: audioGraph.contextState(),
      });
      dispatch({ type: "MEDIA_CAPTURED", media });
      stopStream();
      callbacksRef.current.onMediaCaptured?.(media, duration, diagnostics);
    },
    onRecordingError(recErr) {
      spanRef.current.discard();
      dispatch({ type: "CAPTURE_ERROR", error: recErr });
      callbacksRef.current.onRecordingError?.(recErr);
    },
  });

  const browserSupport: BrowserSupport = useMemo(() => {
    if (!streamSupport.supported) return streamSupport;
    if (mode !== "photo" && typeof MediaRecorder === "undefined") {
      return {
        supported: false,
        error:
          "Your browser doesn't support recording. Photo capture may still work, but video and audio modes are unavailable.",
      };
    }
    return { supported: true };
  }, [streamSupport, mode]);

  // loading ↔ ready based on video element mounting.
  useEffect(() => {
    dispatch({ type: "VIDEO_ELEMENT_CHANGED", ready: isVideoElementReady });
  }, [isVideoElementReady]);

  // ── Recording start ──────────────────────────────────────────────────────
  // The recorder captures the audio graph's destination track — so resume the
  // context and check it reached "running" before starting.

  const { resumeForRecording, waitUntilRunning } = audioGraph;
  const { startRecording: startMediaRecording } = recorder;

  const recordingStreamRef = useRef(recordingStream);
  recordingStreamRef.current = recordingStream;
  const rawStreamRef = useRef(stream);
  rawStreamRef.current = stream;

  /** A start is waiting on the graph; a second call would race it. */
  const awaitingGraphRef = useRef(false);

  const startRecording = useCallback(() => {
    if (awaitingGraphRef.current) return;

    if (resumeForRecording().ready) {
      startMediaRecording(recordingStreamRef.current);
      return;
    }

    awaitingGraphRef.current = true;
    void waitUntilRunning().then(({ ready, contextState }) => {
      // stopRecording clears the flag to cancel a start still waiting here.
      const cancelled = !awaitingGraphRef.current;
      awaitingGraphRef.current = false;
      if (cancelled || !mountedRef.current) return;
      if (ready) {
        startMediaRecording(recordingStreamRef.current);
        return;
      }
      // Report and start anyway with the raw stream
      reportAudioGraphNotRunning(
        { captureSurface, dialogueId },
        contextState,
        GRAPH_READY_TIMEOUT_MS
      );
      startMediaRecording(rawStreamRef.current);
    });
  }, [
    resumeForRecording,
    waitUntilRunning,
    startMediaRecording,
    captureSurface,
    dialogueId,
  ]);

  const { stopRecording: stopMediaRecording } = recorder;
  const stopRecording = useCallback(() => {
    // Also cancels a start still waiting on the graph, which would otherwise
    // begin recording after the student asked to stop.
    awaitingGraphRef.current = false;
    stopMediaRecording();
  }, [stopMediaRecording]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const startCamera = useCallback(
    async (preferredDevices?: PreferredDevices) => {
      if (preferredDevices != null) {
        lastPreferredDevicesRef.current = preferredDevices;
      }
      const profile = videoProfileForMode(mode, await getVideoProfile());
      const { videoConstraints, audioConstraints } = buildStreamConstraints(
        mode,
        effectiveCameraEnabled,
        preferredDevices ?? lastPreferredDevicesRef.current,
        profile.constraints
      );
      await startStream({ videoConstraints, audioConstraints });
    },
    [mode, effectiveCameraEnabled, startStream, getVideoProfile]
  );

  const stopCamera = useCallback(() => stopStream(), [stopStream]);

  // ── Photo capture ─────────────────────────────────────────────────────────

  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  const capturePhotoAction = useCallback(async () => {
    const videoEl = videoElementRef.current;
    if (videoEl == null || stream == null) return;

    const result = await capturePhotoUtil(videoEl, {
      maxFileSize,
      quality: 0.92,
      mimeType: "image/jpeg",
    });

    // Hook may have unmounted while canvas.toBlob was in flight. Revoke the
    // blob URL ourselves since the cleanup effect already ran.
    if (!mountedRef.current) {
      if (result.ok) URL.revokeObjectURL(result.media.url);
      return;
    }

    if (!result.ok) {
      dispatch({
        type: "CAPTURE_ERROR",
        error: { kind: "photo_failed", message: result.error.message },
      });
      return;
    }

    dispatch({ type: "MEDIA_CAPTURED", media: result.media });
    stopStream();
    callbacksRef.current.onMediaCaptured?.(result.media, 0);
  }, [maxFileSize, stream, stopStream]);

  // ── Discard ───────────────────────────────────────────────────────────────
  // Blob URL is revoked by the cleanup effect when capturedMedia.url changes.
  // Stream restart on "recorded → ready" is handled by the post-capture effect
  // below so retake and mode-switch share the same path.

  const discardCapture = useCallback(() => {
    dispatch({ type: "DISCARD" });
  }, []);

  // ── Mode ──────────────────────────────────────────────────────────────────

  const setMode = useCallback((newMode: CaptureMode) => {
    dispatch({ type: "SET_MODE", mode: newMode });
    // Stream restart handled by the mode/camera change effect below.
  }, []);

  // ── Camera toggle ─────────────────────────────────────────────────────────

  const toggleCamera = useCallback(() => {
    if (captureState === "recording") {
      console.warn("Cannot toggle camera while recording.");
      return;
    }
    dispatch({ type: "TOGGLE_CAMERA" });
    // Stream restart handled by the mode/camera change effect below.
  }, [captureState]);

  // ── Mic toggle ────────────────────────────────────────────────────────────

  const toggleMic = useCallback(() => {
    if (captureState === "recording") {
      console.warn("Cannot toggle microphone while recording.");
      return;
    }
    const next = !micEnabled;
    dispatch({ type: "TOGGLE_MIC" });
    setAudioEnabled(next);
  }, [captureState, micEnabled, setAudioEnabled]);

  // ── Video element ref proxy ────────────────────────────────────────────────

  const videoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      videoElementRef.current = node;
      mediaStreamVideoRef(node);
    },
    [mediaStreamVideoRef]
  );

  // ── Restart stream on mode / camera change / post-capture ────────────────
  // Handles three triggers that require a fresh getUserMedia call:
  //   - mode change (photo ↔ video ↔ audio): video/audio tracks differ
  //   - camera toggle: add or remove the video track
  //   - leaving "recorded" state: stream was torn down at capture; user
  //     intends to keep capturing (retake or mode switch after capture)

  const prevModeRef = useRef(mode);
  const prevCameraEnabledRef = useRef(effectiveCameraEnabled);
  const prevCaptureStateRef = useRef(captureState);

  useEffect(() => {
    const modeChanged = mode !== prevModeRef.current;
    const cameraChanged =
      effectiveCameraEnabled !== prevCameraEnabledRef.current;
    const leftRecorded =
      prevCaptureStateRef.current === "recorded" && captureState === "ready";
    // Mode change out of "error" should re-attempt the stream even though
    // there is no live stream to renegotiate.
    const recoveringFromError =
      prevCaptureStateRef.current === "error" && captureState !== "error";

    prevModeRef.current = mode;
    prevCameraEnabledRef.current = effectiveCameraEnabled;
    prevCaptureStateRef.current = captureState;

    if (!modeChanged && !cameraChanged && !leftRecorded && !recoveringFromError)
      return;
    // Skip when there is nothing to renegotiate: no live stream and we did
    // not just leave "recorded" or recover from an error (callers start the
    // initial stream themselves).
    if (stream == null && !leftRecorded && !recoveringFromError) return;

    void getVideoProfile().then((resolvedProfile) => {
      const profile = videoProfileForMode(mode, resolvedProfile);
      const { videoConstraints, audioConstraints } = buildStreamConstraints(
        mode,
        effectiveCameraEnabled,
        lastPreferredDevicesRef.current,
        profile.constraints
      );
      void startStream({ videoConstraints, audioConstraints });
    });
  }, [
    mode,
    effectiveCameraEnabled,
    captureState,
    stream,
    startStream,
    getVideoProfile,
  ]);

  // ── Blob URL cleanup ──────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (capturedMedia?.url != null) {
        URL.revokeObjectURL(capturedMedia.url);
      }
    };
  }, [capturedMedia?.url]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const isRecording = captureState === "recording";
  const hasPermission =
    captureState === "started" ||
    captureState === "recording" ||
    captureState === "recorded";

  useDeviceChangeBreadcrumbs(hasPermission, captureSurface);

  return {
    state: captureState,
    capturedMedia,
    error,

    mode,
    isRecording,
    isProcessing: recorder.isProcessing,
    recordingDuration: recorder.recordingDuration,
    maxDurationSeconds: resolvedRecordingConfig.maxDurationSeconds,
    hasPermission,
    isVideoElementReady,
    // The raw capture stream. The audio graph's destination track — what
    // startRecording actually records — stays internal, so Socratic's
    // preparation recorder, which calls useMediaRecorder directly with this
    // stream, records the raw microphone instead. That split is deliberate.
    //
    // It stays for two reasons that do not expire. Preparation is immune to the
    // suspended-context bug for free, because the readiness check lives in
    // startRecording and preparation never calls it. And unifying the two would
    // widen this return and push that check down into useMediaRecorder, which
    // serves every video answer flow, not just Socratic.
    //
    // Precondition on any future unification: not before the audio level
    // telemetry is live in production.
    stream,
    browserSupport,
    isStartingCamera,
    cameraEnabled: effectiveCameraEnabled,
    micEnabled,

    startCamera,
    stopCamera,
    capturePhoto: capturePhotoAction,
    startRecording,
    stopRecording,
    setMode,
    discardCapture,
    toggleCamera,
    toggleMic,

    videoRef,
  };
}
